import {Axios} from 'services/axios';

export const slotApi = {
  /**
   * Create new slot
   * @param {Object} slotData - Slot data
   * @returns {Promise} - Created slot data
   */
  createSlot: async slotData => {
    try {
      const response = await Axios.post('/slots', slotData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error creating slot:', error);

      let errorMessage = 'Failed to create slot';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Update existing slot
   * @param {string} id - Slot ID
   * @param {Object} slotData - Slot data
   * @returns {Promise} - Updated slot data
   */
  updateSlot: async (id, slotData) => {
    try {
      if (!id) throw new Error('Slot ID is required for update');

      const response = await Axios.put(`/slots/${id}`, slotData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error updating slot:', error);

      let errorMessage = 'Failed to update slot';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Get all slots with pagination and filters
   * @param {Object} options - Query options
   * @returns {Promise} - Slots list with pagination
   */
  getAllSlots: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        search = '',
        category = '',
        isActive = '',
        sortBy = 'displayOrder',
        sortOrder = 'ASC',
        includeInactive = 'false',
      } = options;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
        includeInactive,
      });

      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (isActive !== '') params.append('isActive', isActive);

      const response = await Axios.fetch(`/slots?${params}`);

      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};

      return {
        slots: responseData.slots || [],
        pagination: responseData.pagination || {
          total: 0,
          page: 1,
          limit: 20,
          totalPages: 0,
        },
      };
    } catch (error) {
      console.error('Error fetching slots:', error);

      let errorMessage = 'Failed to fetch slots';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Get slot by ID
   * @param {string} id - Slot ID
   * @returns {Promise} - Slot data
   */
  getSlotById: async id => {
    try {
      const response = await Axios.fetch(`/slots/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error fetching slot:', error);

      let errorMessage = 'Failed to fetch slot';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Get active slots (public)
   * @param {string} countryCode - Country code for filtering
   * @returns {Promise} - Active slots
   */
  getActiveSlots: async (countryCode = null) => {
    try {
      // let url = '/slots/public/active';
      // if (countryCode) {
      //   url += `?countryCode=${countryCode}`;
      // }
      // const response = await Axios.fetch(url);
      // return response.data;
    } catch (error) {
      console.error('❌ Error fetching active slots:', error);

      let errorMessage = 'Failed to fetch active slots';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Delete slot (soft delete)
   * @param {string} id - Slot ID
   * @returns {Promise} - Deletion response
   */
  deleteSlot: async id => {
    try {
      const response = await Axios.delete(`/slots/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error deleting slot:', error);

      let errorMessage = 'Failed to delete slot';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Get slot stats
   * @param {string} slotId - Slot ID
   * @returns {Promise} - Slot statistics
   */
  getSlotStats: async slotId => {
    try {
      const response = await Axios.fetch(`/slots/${slotId}/stats`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error fetching slot stats:', error);

      let errorMessage = 'Failed to fetch slot stats';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Add country override for slot
   * @param {string} slotId - Slot ID
   * @param {Object} overrideData - Country override data
   * @returns {Promise} - Created override
   */
  addCountryOverride: async (slotId, overrideData) => {
    try {
      const response = await Axios.post(
        `/slots/${slotId}/country-override`,
        overrideData,
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error adding country override:', error);

      let errorMessage = 'Failed to add country override';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Remove country override
   * @param {string} slotId - Slot ID
   * @param {string} countryId - Country ID
   * @returns {Promise} - Removal response
   */
  removeCountryOverride: async (slotId, countryId) => {
    try {
      const response = await Axios.delete(
        `/slots/${slotId}/country-override/${countryId}`,
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error removing country override:', error);

      let errorMessage = 'Failed to remove country override';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },

  /**
   * Get slot categories
   * @returns {Promise} - List of categories
   */
  getSlotCategories: async () => {
    try {
      const response = await Axios.fetch('/slots/categories');
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ Error fetching slot categories:', error);

      let errorMessage = 'Failed to fetch slot categories';
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  },
};
